/* **The GHOST — an outlined, un-filled secondary.** The third of this app's three button
 * treatments, beside `emberButton.module.css` (the one real action) and `inkButton.module.css`
 * (a filled primary in ink where the accent is already spent elsewhere).
 *
 * A stylesheet with no component, for the reason `photoBox.module.css` gives: a component would
 * have to take the geometry as props, and the client detail draws this control at two sizes — a
 * `--h-detail-btn` one on the tabs and a `--h-detail-tiny-btn` one in the summary column's cards.
 * That is a prop bag around a `<button>`.
 *
 * ── What is here, and what stays on the surface — `inkButton`'s contract exactly ─────────────
 * Two classes on the element: this one for the TREATMENT — the ink, the hairline edge, the cursor
 * and the two states — and the surface's own for the GEOMETRY, declared there as ordinary
 * properties:
 *
 *   height          padding          border-radius
 *   font-size       font-weight      background
 *
 * `background` is geometry here rather than treatment, and that is the one departure from the ink
 * button's list: a ghost on the tab canvas is transparent and a ghost inside a card is painted
 * `--bg-card`, so the fill is a fact about where the control stands.
 *
 * **Nothing may be declared on both sides.** A declaration living in both a shared module and a
 * page's own is settled by CHUNK ORDER, which differs between the dev server and the build — that
 * cost a focus ring that was neutral in development and accent in the shipped bundle, measured on
 * 5 August 2026 and written up at length in `emberButton.module.css`.
 *
 * ── Why this file exists at all ──────────────────────────────────────────────────────────────
 * These fourteen declarations were written out three times, in `DetailTab.module.css`,
 * `OverviewTab.module.css` and `SummaryColumn.module.css` — three stylesheets belonging to ONE
 * screen. Two of the three were byte-identical and the third differed only in its geometry, which
 * is precisely the near-duplicate the styling rules exist to stop: the next change to the hairline
 * or the refused-state ink would have landed in one file and not the other two.
 *
 * Flat, no shadow, no glow, nothing moves in Z. */

.ghost {
  display: inline-flex;
  flex: none;
  align-items: center;
  font-family: var(--font-body);
  line-height: var(--lh-auto);
  color: var(--text-2);
  cursor: pointer;
  border: var(--border-hairline) solid var(--border-2);
}

/* Focusable while refused. A control carrying a reason has to stay in the tab order for the reason
 * to be reachable at all (view ADR §12), which is why `aria-disabled` is styled beside the real
 * attribute rather than instead of it — the two mean different things and both must look refused.
 *
 * The ink is `--text-2`, which is what the ink button, the ember button and the confirm prompt all
 * refuse in. This one reached for `--text-3` and was the odd one out: measured at 2.34:1 on the rule
 * sheet's „Wyczyść nadpisania", which is a refusal a trainer cannot read. A refused control still
 * has to say what it is — dimming is the signal, illegibility is a defect.
 *
 * ── WHERE THE REFUSAL IS THEN VISIBLE, since the ink can no longer carry it ──────────────────
 * `--text-2` is also this treatment's RESTING ink, so ink alone now says nothing: raising it to a
 * legible value left „Wyczyść nadpisania" with no pins looking exactly like „Anuluj" beside it, and
 * a refusal a trainer cannot SEE is the same defect pointed the other way. So the fill and the edge
 * carry it, in the ink and ember buttons' own two tokens — `--bg-chip` over the surface's own
 * ground, `--border` in place of the resting `--border-2`. Three treatments, one refused look.
 *
 * `background` is the surface's to declare on every other frame (see the header), and this is the
 * one rule that takes it back, because a ghost that refuses has to stop reading as whatever it
 * stands on. It is not a declaration on both sides: an attribute selector outranks the surface's
 * single class whichever chunk lands first, so the outcome does not depend on the build. */
.ghost[aria-disabled='true'],
.ghost:disabled {
  color: var(--text-2);
  background: var(--bg-chip);
  border-color: var(--border);
  cursor: not-allowed;
}

/* `--accent` on a neutral, unfilled control: a neutral ring on a neutral edge is the one
 * combination that disappears. The ink button's ring follows the same rule for the same reason. */
.ghost:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Two modifiers: the OUTLINE pills of `/konto` (owner-approved polish, 4 November 2026) ──────
 * Worn as `.ghost.ink` (a secondary act in body ink) and `.ghost.danger` (a destructive act, never
 * filled and never primary), with `pillButton`'s shape and the surface's size beside them. Each
 * takes `background` back from the surface, the one departure from the header's contract: an
 * outline pill shows whatever it stands on, on the sheet and on a group card alike. Refused, it is
 * a transparent outline like `emberButton`'s `.quiet` primary beside it, not the chip fill above:
 * the screen's three paints refuse in one look. The modifier rules out-rank `.ghost[aria-disabled]`
 * by specificity, so the outcome does not depend on order within this file either. */
.ghost.ink {
  color: var(--text);
  background: transparent;
}

.ghost.danger {
  color: var(--danger-text-strong);
  background: transparent;
  border-color: var(--danger-border);
}

.ghost.ink[aria-disabled='true'],
.ghost.ink:disabled,
.ghost.danger[aria-disabled='true'],
.ghost.danger:disabled {
  color: var(--text-3);
  background: transparent;
  border-color: var(--border);
}

.ghost.ink:hover:not([aria-disabled='true'], :disabled) {
  border-color: var(--text-3);
}

.ghost.danger:hover:not([aria-disabled='true'], :disabled) {
  border-color: var(--danger-text-strong);
}
